export class ApiError extends Error {
  constructor(message, { status, body, errors } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.errors = errors || [];
  }
}

export const SECURITY_STEP_UP_REQUIRED_CODE = 68001;
export const SENSITIVE_ACTION_RESTRICTED_CODE = 68002;
export const INVALID_SECURITY_STEP_UP_CODE = 67002;
export const TENANT_RESTRICTED_MODE_ACTIVE_CODE = 70001;

function normalizeApiErrorEntries(errors) {
  if (Array.isArray(errors)) {
    return errors;
  }

  if (errors && typeof errors === 'object') {
    if ('code' in errors || 'message' in errors || 'error' in errors) {
      return [errors];
    }

    return Object.values(errors).flatMap((item) => (Array.isArray(item) ? item : [item]));
  }

  return [];
}

export function getApiErrorCodes(error) {
  return [
    ...normalizeApiErrorEntries(error?.errors),
    ...normalizeApiErrorEntries(error?.body?.errors),
    ...normalizeApiErrorEntries(error?.body),
    ...normalizeApiErrorEntries(error),
  ]
    .map((item) => item?.code)
    .filter((code) => code !== undefined && code !== null && String(code).trim() !== '');
}

export function hasApiErrorCode(error, expectedCode) {
  const normalizedExpected = String(expectedCode);
  return getApiErrorCodes(error).some((code) => String(code) === normalizedExpected);
}

export function isSecurityStepUpRequiredError(error) {
  return hasApiErrorCode(error, SECURITY_STEP_UP_REQUIRED_CODE);
}

export function isSensitiveActionRestrictedError(error) {
  return hasApiErrorCode(error, SENSITIVE_ACTION_RESTRICTED_CODE);
}

export function isInvalidSecurityStepUpCodeError(error) {
  return hasApiErrorCode(error, INVALID_SECURITY_STEP_UP_CODE);
}

export function isTenantRestrictedModeActiveError(error) {
  return hasApiErrorCode(error, TENANT_RESTRICTED_MODE_ACTIVE_CODE);
}

export class HttpClient {
  constructor({ baseUrl, defaultHeaders } = {}) {
    this.baseUrl = (baseUrl || '').replace(/\/$/, '');
    this.defaultHeaders = defaultHeaders || { 'Content-Type': 'application/json' };
  }

  resolveUrl(path) {
    if (/^https?:\/\//.test(path)) {
      return path;
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}`;
  }

  static buildQueryString(query = {}) {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  static parseApiErrors(parsedBody) {
    const nestedErrors = normalizeApiErrorEntries(parsedBody?.errors);
    const topLevelErrors = parsedBody && typeof parsedBody === 'object' && parsedBody.code !== undefined
      ? normalizeApiErrorEntries(parsedBody)
      : [];
    const rawErrors = nestedErrors.length > 0
      ? nestedErrors
      : topLevelErrors;
    const errors = rawErrors
      .map((item) => {
        if (!item) {
          return null;
        }

        if (typeof item === 'string') {
          return { message: item, parameter: '', value: '' };
        }

        if (typeof item === 'object') {
          return {
            ...(item.code !== undefined && item.code !== null ? { code: item.code } : {}),
            message: String(item.message || item.error || '').trim(),
            parameter: String(item.parameter || item.field || '').trim(),
            value: item.value === undefined || item.value === null ? '' : String(item.value),
          };
        }

        return null;
      })
      .filter(Boolean);

    const formatErrorCode = (code) => (
      code === undefined || code === null || String(code).trim() === ''
        ? ''
        : String(code).trim()
    );

    const baseMessage =
      (parsedBody && parsedBody.message) ||
      (parsedBody && parsedBody.error) ||
      (parsedBody && parsedBody.detail) ||
      '';

    const detailsMessage = errors
      .map((item) => {
        const parts = [];
        const code = formatErrorCode(item.code);
        if (item.parameter) {
          parts.push(item.parameter);
        }
        if (item.message) {
          parts.push(item.message);
        }
        if (item.value) {
          parts.push(`value: ${item.value}`);
        }
        const detail = parts.join(' - ');
        return code ? [code, detail].filter(Boolean).join(': ') : detail;
      })
      .filter(Boolean)
      .join('; ');

    const message =
      [String(baseMessage || '').trim(), detailsMessage]
        .filter(Boolean)
        .join('. ') || 'Operation failed';

    return {
      message,
      errors,
    };
  }

  async request(path, options = {}) {
    const { query, headers, body, ...rest } = options;
    const queryString = HttpClient.buildQueryString(query);

    const isFormData = body instanceof FormData;
    const mergedHeaders = { ...this.defaultHeaders, ...(headers || {}) };
    if (isFormData) {
      delete mergedHeaders['Content-Type'];
    }

    const response = await fetch(this.resolveUrl(`${path}${queryString}`), {
      ...rest,
      headers: mergedHeaders,
      ...(body !== undefined
        ? {
            body: typeof body === 'string' || isFormData ? body : JSON.stringify(body),
          }
        : {}),
    });

    const rawBody = await response.text();
    let parsedBody = null;

    if (rawBody) {
      try {
        parsedBody = JSON.parse(rawBody);
      } catch {
        parsedBody = rawBody;
      }
    }

    const hasResultFalse =
      parsedBody &&
      typeof parsedBody === 'object' &&
      'result' in parsedBody &&
      parsedBody.result === false;

    if (!response.ok || hasResultFalse) {
      const { message, errors } = HttpClient.parseApiErrors(parsedBody);
      const fallbackMessage = !response.ok ? `Request failed with status ${response.status}` : 'Operation failed';

      throw new ApiError(message || fallbackMessage, {
        status: response.status,
        body: parsedBody,
        errors,
      });
    }

    return parsedBody;
  }
}
